import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  AgeGradeInstrument,
  AgeGradeInstrumentCode,
} from '@core/enums/age-grade-instruments';
import { ISearchInput } from '@core/interfaces/isearch-input';
import { AuthService } from '@core/services/auth.service';
import { ToastService } from '@core/services/toast.service';
import { forkJoin, take, tap } from 'rxjs';
import { IRatingBaseTemplate } from './interfaces/irating-base-template';
import { IRatingViewEnrollment } from './interfaces/irating-view-enrollment';
import { Permission } from '@core/enums/permissions';
import { AgeGradeTemplateService } from '@core/services/age-grade-template.service';
import { AgeGradeTemplateEnrollmentService } from '@core/services/age-grade-template-enrollment.service.ts';
import { Router } from '@angular/router';
import { MatTableDataSource } from '@angular/material/table';
import { TableColumnType } from '@core/enums/table';
import { ICustomRatingTemplate } from './interfaces/icustom-rating-template';
import { PermissionService } from '@core/services/permission.service';
import { AgencyService } from '@core/services/agency.service';
import { IAgency } from '@core/interfaces/iagency';
import { LookupService } from '@core/services/lookup.service';
import { IAgeGradeInstrument } from '@core/interfaces/iage-grade-instrument';
import { ILoginResponse } from '@core/interfaces/ilogin-response';
import { Modal } from '@core/enums/modal';
import { ConfirmationModalComponent } from '@shared/components/confirmation-modal/confirmation-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { RatingPeriodService } from '@core/services/rating-period.service';
import { IModalEmitData } from '@core/interfaces/imodal';
import { Subscription } from 'rxjs';
import { ClassService } from '@core/services/class.service';
import { States } from '@core/enums/states';
import { SiteService } from '@core/services/site.service';
import { ClassroomService } from '@core/services/classroom.service';
import { MeasureRevision } from '@core/enums/measure-revision';
import { ActivatedRoute } from '@angular/router';
import { StateService } from '@core/services/state.service';
import { IState } from '@core/interfaces/istate';
import { RatingViewVersionDefaults } from '@core/enums/rating-view-version-defaults';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'drdp-rating-view-setup',
    templateUrl: './rating-view-setup.component.html',
    styleUrls: ['./rating-view-setup.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class RatingViewSetupComponent implements OnInit {
  defaultViewForm: FormGroup | any;
  fieldStudyForm: FormGroup | any;
  historical2015Form: FormGroup | any;
  historicalEAForm: FormGroup | any;
  psOptions: IRatingBaseTemplate[] = [];
  itOptions: IRatingBaseTemplate[] = [];
  kOptions: IRatingBaseTemplate[] = [];
  saOptions: IRatingBaseTemplate[] = [];
  tkOptions: IRatingBaseTemplate[] = [];
  pilotItOptions: IRatingBaseTemplate[] = [];
  ptkOptions: IRatingBaseTemplate[] = [];
  p3Options: IRatingBaseTemplate[] = [];
  pilotSaOptions: IRatingBaseTemplate[] = [];
  historicalEAOptions: IRatingBaseTemplate[] = [];
  histEAItOptions: IRatingBaseTemplate[] = [];
  histEAPtkOptions: IRatingBaseTemplate[] = [];
  histEAP3Options: IRatingBaseTemplate[] = [];
  histPsOptions: IRatingBaseTemplate[] = [];
  histItOptions: IRatingBaseTemplate[] = [];
  histKOptions: IRatingBaseTemplate[] = [];
  histSaOptions: IRatingBaseTemplate[] = [];
  histTkOptions: IRatingBaseTemplate[] = [];
  hasIT = false;
  hasPS = false;
  hasK = false;
  hasSA = false;
  hasTK = false;
  selectedAgencyId = 0;
  selectedStateId = 0;
  show2015Views = false;
  show2025Views = false;
  showHistorical2015Views = false;
  showHistoricalEAViews = false;
  ageGradeInstrument = AgeGradeInstrument;
  permission = Permission;
  dataSource: MatTableDataSource<any> = new MatTableDataSource();
  customTemplates: ICustomRatingTemplate[] = [];
  ageGradeInstruments: IAgeGradeInstrument[] = [];
  canEditRatingViews = false;
  canUnlockRatingViews = false;
  agency: IAgency | null = null;
  currentUser?: ILoginResponse;
  dialog = inject(MatDialog);
  ratingPeriodService = inject(RatingPeriodService);
  schoolYearName: string = '';
  pilotAgeGradeInstruments: IAgeGradeInstrument[] = [];
  states = States;
  showAlternateRatingViews: boolean = false;
  p3Info = '';
  schoolAgeInfo = '';

  customTableColumns: any[] = [];

  get itTemplateId() {
    return this.defaultViewForm.get('itTemplateId');
  }
  get psTemplateId() {
    return this.defaultViewForm.get('psTemplateId');
  }

  get tkTemplateId() {
    return this.defaultViewForm.get('tkTemplateId');
  }
  get saTemplateId() {
    return this.defaultViewForm.get('saTemplateId');
  }

  get kTemplateId() {
    return this.defaultViewForm.get('kTemplateId');
  }

  get pilotItTemplateId() {
    return this.fieldStudyForm.get('pilotItTemplateId');
  }

  get ptkTemplateId() {
    return this.fieldStudyForm.get('ptkTemplateId');
  }

  get p3TemplateId() {
    return this.fieldStudyForm.get('p3TemplateId');
  }

  get pilotSaTemplateId() {
    return this.fieldStudyForm.get('pilotSaTemplateId');
  }

  get isFormDisabled(): boolean {
    return !this.canEditRatingViews || !!this.agency?.isRatingViewLocked;
  }

  public get state() {return States }

  constructor(
    public modal: MatDialog,
    private authService: AuthService,
    private formBuilder: FormBuilder,
    private agencyService: AgencyService,
    private siteService: SiteService,
    private classroomService: ClassroomService,
    private templateService: AgeGradeTemplateService,
    private templateEnrollmentService: AgeGradeTemplateEnrollmentService,
    private toast: ToastService,
    private router: Router,
    private permissionService: PermissionService,
    private lookupService: LookupService,
    private classService: ClassService,
    public PermissionService: PermissionService,
    private activatedRoute: ActivatedRoute,
    private stateService: StateService,
    private translocoService: TranslocoService
  ) {
    this.currentUser = this.authService.getCurrentUser();
    this.selectedAgencyId =
      this.router.getCurrentNavigation()?.extras?.state?.['agencyId'] ||
      this.currentUser.agencyId;
    this.selectedStateId =
      this.router.getCurrentNavigation()?.extras?.state?.['stateId'] ||
      this.currentUser.stateId;
  }
  private subscriptions = new Subscription();
  canDelete = this.PermissionService.checkPermission(Permission.DeleteRatingView);
  receivedPayload?: any;

  canSearch = false;
  canViewResults = true;
  searchInput: ISearchInput = {
    searchName: false,
    searchAgency: true,
    searchState: true,
  };
  agencyBaseEnrollments: IRatingViewEnrollment[] = [];
  historical2015Enrollments: IRatingViewEnrollment[] = [];
  historicalEAEnrollments: IRatingViewEnrollment[] = [];

  ngOnInit(): void {
    this.p3Info = this.translocoService.translate('configuration.ratingViewSetup.p3Info');
    this.schoolAgeInfo = this.translocoService.translate('configuration.ratingViewSetup.schoolAgeInfo');
    this.customTableColumns = [
      {
        columnDef: 'name',
        header: this.translocoService.translate('configuration.common.title'),
        type: TableColumnType.text,
      },
      {
        columnDef: 'ratingPeriod',
        header: this.translocoService.translate('configuration.common.ratingPeriod'),
        type: TableColumnType.text,
      },
      {
        columnDef: 'ageGradeInstrument',
        header: this.translocoService.translate('configuration.common.ageGroup'),
        type: TableColumnType.text,
      },
      {
        columnDef: 'description',
        header: this.translocoService.translate('configuration.common.description'),
        type: TableColumnType.text,
      },
      {
        columnDef: 'baseDescription',
        header: this.translocoService.translate('configuration.common.baseTemplate'),
        type: TableColumnType.text,
      },
      {
        columnDef: 'View',
        header: this.translocoService.translate('configuration.common.view'),
        type: TableColumnType.view,
      }
    ];
    this.initData();
    this.getStateRatingViewConfiguration();
    this.canSearch =
      this.authService.isSuperAdmin() || this.authService.isCustomerService();
    this.canViewResults = !this.canSearch;

    this.canEditRatingViews = this.permissionService.checkPermission(
      Permission.EditRatingViews
    );

    this.canUnlockRatingViews = this.permissionService.checkPermission(
      Permission.UnlockRatingViews
    );
    this.initForm();
    this.initData();

    if (this.canDelete) {
      this.customTableColumns.push({ columnDef: 'delete', header: this.translocoService.translate('configuration.common.delete'), type: TableColumnType.delete })
    }
  }

  initForm() {
    this.defaultViewForm = this.formBuilder.group({
      itTemplateId: [null],
      psTemplateId: [null],
      kTemplateId: [null],
      saTemplateId: [null],
      tkTemplateId: [null],
    });

    this.fieldStudyForm = this.formBuilder.group({
      pilotItTemplateId: [null],
      ptkTemplateId: [null],
      p3TemplateId: [null],
      pilotSaTemplateId: [null],
      ratingLevel: [null],
      siteIds: [[]],
      classroomIds: [[]]
    });

    this.historical2015Form = this.formBuilder.group({
      histItTemplateId: [null],
      histPsTemplateId: [null],
      histKTemplateId: [null],
      histSaTemplateId: [null],
      histTkTemplateId: [null],
    });

    this.historicalEAForm = this.formBuilder.group({
      histEAItTemplateId: [null],
      histEAPtkTemplateId: [null],
      histEAP3TemplateId: [null],
    });

    this.updateFormDisabledState();
    this.getAgencyInfo(this.selectedAgencyId);
  }

  private updateFormDisabledState(): void {
    const shouldDisable = !this.canEditRatingViews || !!this.agency?.isRatingViewLocked;
    
    if (shouldDisable) {
      this.defaultViewForm.disable();
      this.fieldStudyForm.disable();
    } else {
      this.defaultViewForm.enable();
      this.fieldStudyForm.enable();
    }
    
    this.historical2015Form.disable();
    this.historicalEAForm.disable();
  }

  private clearFieldStudyValidators(): void {
    const controls = [
      'pilotItTemplateId',
      'ptkTemplateId', 
      'p3TemplateId',
      'pilotSaTemplateId',
      'ratingLevel',
      'siteIds',
      'classroomIds'
    ];

    controls.forEach(controlName => {
      const control = this.fieldStudyForm.get(controlName);
      control?.clearValidators();
      control?.updateValueAndValidity();
    });
  }

  private clearDefaultFormValidators(): void {
    const controls = [
      'itTemplateId',
      'psTemplateId',
      'kTemplateId',
      'saTemplateId',
      'tkTemplateId'
    ];

    controls.forEach(controlName => {
      const control = this.defaultViewForm.get(controlName);
      control?.clearValidators();
      control?.updateValueAndValidity();
    });
  }

  private clearAllValidators(): void {
    this.clearDefaultFormValidators();
    this.clearFieldStudyValidators();
  }

  private set2015Validators(): void {
    if (this.hasIT) {
      this.itTemplateId?.setValidators(Validators.required);
      this.itTemplateId?.updateValueAndValidity();
    }
    if (this.hasPS) {
      this.psTemplateId?.setValidators(Validators.required);
      this.psTemplateId?.updateValueAndValidity();
    }
    if (this.hasK) {
      this.kTemplateId?.setValidators(Validators.required);
      this.kTemplateId?.updateValueAndValidity();
    }
    if (this.hasSA) {
      this.saTemplateId?.setValidators(Validators.required);
      this.saTemplateId?.updateValueAndValidity();
    }
    if (this.hasTK) {
      this.tkTemplateId?.setValidators(Validators.required);
      this.tkTemplateId?.updateValueAndValidity();
    }
  }

  private set2025Validators(): void {
    if (this.show2025Views) {
      this.pilotItTemplateId?.setValidators(Validators.required);
      this.pilotItTemplateId?.updateValueAndValidity();
      this.ptkTemplateId?.setValidators(Validators.required);
      this.ptkTemplateId?.updateValueAndValidity();
      this.p3TemplateId?.setValidators(Validators.required);
      this.p3TemplateId?.updateValueAndValidity();
      this.pilotSaTemplateId?.setValidators(Validators.required);
      this.pilotSaTemplateId?.updateValueAndValidity();
    }
  }

  private updateFormValidators(): void {
    this.clearAllValidators();

    if (this.show2015Views) {
      this.set2015Validators();
    }

    if (this.show2025Views) {
      this.set2025Validators();
    }

    this.defaultViewForm.updateValueAndValidity();
    this.fieldStudyForm.updateValueAndValidity();
  }

  getStateRatingViewConfiguration(): void {
    this.activatedRoute.params.subscribe((params: any) => {
      const stateId = params['stateId'] || this.selectedStateId;
      if (stateId) {
        this.stateService.getStatesById(stateId).pipe(
          take(1)
        ).subscribe((state: IState) => {
          this.selectedStateId = stateId;
          this.updateViewVisibility(state);
        });
      }
    });
  }

  updateViewVisibility(state: IState): void {
    this.show2015Views = state.ratingViewVersionId === RatingViewVersionDefaults.VERSION_2015;
    this.show2025Views = state.ratingViewVersionId === RatingViewVersionDefaults.VERSION_2025;
    this.showAlternateRatingViews = state.showAlternateRatingViews || false;
    
    this.updateFormValidators();
  }

  initData() {
    this.ratingPeriodService
      .getActiveSchoolYear()
      .pipe(take(1))
      .subscribe((res) => {
        this.schoolYearName = res.schoolYearName;
      });

    if (this.canViewResults) {
      this.getBaseTemplates(0);
      this.getAgencyTemplates(0);
      this.getAgeGradeInstruments(0);
    }
  }

  getBaseTemplates(stateId: number) {
    this.templateService
      .getBaseTemplates(stateId)
      .pipe(
        take(1),
        tap((results) => {
          this.psOptions = results.filter(
            (r) => r.ageGradeInstrumentCode == AgeGradeInstrumentCode.preschool
          );
          this.itOptions = results.filter(
            (r) =>
              this.selectedStateId == States.newHampshire ?
                r.ageGradeInstrumentId == AgeGradeInstrument.nhInfantToddler :
                r.ageGradeInstrumentId == AgeGradeInstrument.infantToddler
          );
          this.kOptions = results.filter(
            (r) =>
              r.ageGradeInstrumentCode == AgeGradeInstrumentCode.kindergarten
          );
          this.saOptions = results.filter(
            (r) => r.ageGradeInstrumentId == AgeGradeInstrument.schoolAge
          );
          this.tkOptions = results.filter(
            (r) =>
              r.ageGradeInstrumentCode ==
              AgeGradeInstrumentCode.transitionalKinder
          );

          this.pilotItOptions = results.filter(
            (r) =>
              r.ageGradeInstrumentId == AgeGradeInstrument.pilotInfantToddler && 
              (r.measureRevisionId == MeasureRevision.R2025)
          );
          this.ptkOptions = results.filter(
            (r) =>
              r.ageGradeInstrumentCode == AgeGradeInstrumentCode.pilotPreschool && 
              (r.measureRevisionId == MeasureRevision.R2025)
          );
          this.p3Options = results.filter(
            (r) =>
              r.ageGradeInstrumentCode == AgeGradeInstrumentCode.pilotPreschool3 && 
              (r.measureRevisionId == MeasureRevision.R2025)
          );
          this.pilotSaOptions = results.filter(
            (r) =>
              r.ageGradeInstrumentId == AgeGradeInstrument.pilotSchoolAge && r.measureRevisionId == MeasureRevision.R2025
          );
        })
      )
      .subscribe();

    // Historical options need to include templates that are hidden
    this.templateService
      .getBaseTemplates(stateId, true)
      .pipe(
        take(1),
        tap((results) => {
          this.histPsOptions = results.filter(
            (r) => r.ageGradeInstrumentCode == AgeGradeInstrumentCode.preschool
          );
          this.histItOptions = results.filter(
            (r) =>
              (this.selectedStateId == States.newHampshire ?
                r.ageGradeInstrumentId == AgeGradeInstrument.nhInfantToddler :
                r.ageGradeInstrumentId == AgeGradeInstrument.infantToddler)
          );
          this.histKOptions = results.filter(
            (r) =>
              r.ageGradeInstrumentCode == AgeGradeInstrumentCode.kindergarten
          );
          this.histSaOptions = results.filter(
            (r) =>
              r.ageGradeInstrumentId == AgeGradeInstrument.schoolAge
          );
          this.histTkOptions = results.filter(
            (r) =>
              r.ageGradeInstrumentCode == AgeGradeInstrumentCode.transitionalKinder
          );

          // Historical Early Adopter options (R2025SoftImplementation)
          this.histEAItOptions = results.filter(
            (r) =>
              r.ageGradeInstrumentId == AgeGradeInstrument.pilotInfantToddler &&
              r.measureRevisionId == MeasureRevision.R2025SoftImplementation
          );
          this.histEAPtkOptions = results.filter(
            (r) =>
              r.ageGradeInstrumentCode == AgeGradeInstrumentCode.pilotPreschool &&
              r.measureRevisionId == MeasureRevision.R2025SoftImplementation
          );
          this.histEAP3Options = results.filter(
            (r) =>
              r.ageGradeInstrumentCode == AgeGradeInstrumentCode.pilotPreschool3 &&
              r.measureRevisionId == MeasureRevision.R2025SoftImplementation
          );
        })
      )
      .subscribe();
  }

  getAgencyInfo(agencyId: number) {
    this.agencyService
      .getAgency(agencyId)
      .pipe(take(1))
      .subscribe((agency) => {
        this.agency = agency;
        this.updateFormDisabledState();
      });
  }

  unlockAgency() {
    this.agencyService
      .unlockAgency(this.selectedAgencyId)
      .pipe(take(1))
      .subscribe((res) => {
        this.getAgencyInfo(this.selectedAgencyId);
      });
  }

  getEnrollmentsByAgencyId(search: any): void {
    if (search.StateId) {
      this.selectedStateId = search.StateId;
      this.getBaseTemplates(search.StateId);
      this.getAgeGradeInstruments(search.StateId);
      this.getStateInfo(search.StateId);
      this.getStateRatingViewConfiguration();

      if (this.selectedStateId !== States.california) {
        this.clearFieldStudyValidators();
      }
    }

    if (search.AgencyId) {
      this.canViewResults = true;
      this.selectedAgencyId = search.AgencyId;
      this.clearFieldStudyValidators();
      this.getAgencyTemplates(search.AgencyId);
      this.getAgencyInfo(search.AgencyId);
    }
  }

  getStateInfo(stateId: number): void {
    this.subscriptions.add(
      this.stateService.getStatesById(stateId).pipe(
        take(1)
      ).subscribe((state: IState) => {
        this.showAlternateRatingViews = state.showAlternateRatingViews || false;
      })
    );
  }

  getAgeGradeInstruments(stateId: number) {
    this.subscriptions.add(
      forkJoin({
        ageGradeInstrument: this.lookupService.getAgeGradeInstruments(stateId),
        pilotAgeGradeInstrument: this.lookupService.getPilotAgeGrade(),
      }).subscribe(({ ageGradeInstrument, pilotAgeGradeInstrument }) => {
        this.ageGradeInstruments = ageGradeInstrument;
        this.pilotAgeGradeInstruments = pilotAgeGradeInstrument;
        this.updateAgeGradeInstruments();
      }
    ));
  }

  updateAgeGradeInstruments() {
    this.hasIT =
      this.ageGradeInstruments.find(
        (agi) =>
          agi.ageGradeInstrumentCode == AgeGradeInstrumentCode.infantToddler
      ) != null;

    this.hasSA =
      this.ageGradeInstruments.find(
        (agi) => agi.ageGradeInstrumentCode == AgeGradeInstrumentCode.schoolAge
      ) != null;

    this.hasPS =
      this.ageGradeInstruments.find(
        (agi) => agi.ageGradeInstrumentCode == AgeGradeInstrumentCode.preschool
      ) != null;

    this.hasTK =
      this.ageGradeInstruments.find(
        (agi) =>
          agi.ageGradeInstrumentCode ==
          AgeGradeInstrumentCode.transitionalKinder
      ) != null;

    this.hasK =
      this.ageGradeInstruments.find(
        (agi) =>
          agi.ageGradeInstrumentCode == AgeGradeInstrumentCode.kindergarten
      ) != null;

    this.updateFormValidators();
  }

  getAgencyTemplates(agencyId: number) {
    this.getAgencyInfo(agencyId);
    this.templateEnrollmentService
      .getBaseTemplateEnrollmentByAgency(agencyId)
      .pipe(
        take(1),
        tap((results) => {
          this.updateFormValue(results);
        })
      )
      .subscribe();

    this.templateService
      .getCustomTemplatesByAgencyId(agencyId)
      .pipe(
        take(1),
        tap((results) => {
          this.customTemplates = results;
          this.dataSource = new MatTableDataSource(results);
        })
      )
      .subscribe();
  }

  updateFormValue(results: IRatingViewEnrollment[]) {
    this.defaultViewForm.reset();
    this.fieldStudyForm.reset();
    this.historical2015Form.reset();
    this.historicalEAForm.reset();
    this.showHistorical2015Views = false;
    this.showHistoricalEAViews = false;

    if (results) {
      this.agencyBaseEnrollments = results.filter((r) => !r.isCustom && !r.isHistorical);
      const historicalEnrollments = results.filter((r) => !r.isCustom && r.isHistorical);
      
      const psOption = this.agencyBaseEnrollments.find(
        (a) => a.ageGradeInstrumentCode == AgeGradeInstrumentCode.preschool
      )?.ageGradeTemplateId;
      const itOption = this.agencyBaseEnrollments.find(
        (a) => 
          this.selectedStateId == States.newHampshire ?
            a.ageGradeInstrumentId == AgeGradeInstrument.nhInfantToddler :
            a.ageGradeInstrumentId == AgeGradeInstrument.infantToddler
      )?.ageGradeTemplateId;
      const kOption = this.agencyBaseEnrollments.find(
        (a) => a.ageGradeInstrumentCode == AgeGradeInstrumentCode.kindergarten
      )?.ageGradeTemplateId;
      const saOption = this.agencyBaseEnrollments.find(
        (a) => a.ageGradeInstrumentId == AgeGradeInstrument.schoolAge
      )?.ageGradeTemplateId;
      const tkOption = this.agencyBaseEnrollments.find(
        (a) =>
          a.ageGradeInstrumentCode == AgeGradeInstrumentCode.transitionalKinder
      )?.ageGradeTemplateId;

      this.defaultViewForm.setValue({
        psTemplateId: psOption ?? null,
        itTemplateId: itOption ?? null,
        kTemplateId: kOption ?? null,
        saTemplateId: saOption ?? null,
        tkTemplateId: tkOption ?? null,
      });

      if (historicalEnrollments.length > 0) {
        this.historical2015Enrollments = historicalEnrollments.filter(h => h.measureRevisionId === MeasureRevision.R2015);
        this.showHistorical2015Views = this.historical2015Enrollments.length > 0;
        
        const histPsOption = this.historical2015Enrollments.find(
          (h) => h.ageGradeInstrumentCode == AgeGradeInstrumentCode.preschool
        )?.ageGradeTemplateId;
        const histItOption = this.historical2015Enrollments.find(
          (h) => 
            this.selectedStateId == States.newHampshire ?
              h.ageGradeInstrumentId == AgeGradeInstrument.nhInfantToddler :
              h.ageGradeInstrumentId == AgeGradeInstrument.infantToddler
        )?.ageGradeTemplateId;
        const histKOption = this.historical2015Enrollments.find(
          (h) => h.ageGradeInstrumentCode == AgeGradeInstrumentCode.kindergarten
        )?.ageGradeTemplateId;
        const histSaOption = this.historical2015Enrollments.find(
          (h) => h.ageGradeInstrumentId == AgeGradeInstrument.schoolAge
        )?.ageGradeTemplateId;
        const histTkOption = this.historical2015Enrollments.find(
          (h) =>
            h.ageGradeInstrumentCode ==
            AgeGradeInstrumentCode.transitionalKinder
        )?.ageGradeTemplateId;

        this.historical2015Form.setValue({
          histPsTemplateId: histPsOption ?? null,
          histItTemplateId: histItOption ?? null,
          histKTemplateId: histKOption ?? null,
          histSaTemplateId: histSaOption ?? null,
          histTkTemplateId: histTkOption ?? null,
        });
      }

      if (historicalEnrollments.length > 0) {
        this.historicalEAEnrollments = historicalEnrollments.filter(h => 
          h.measureRevisionId === MeasureRevision.R2025SoftImplementation
        );
        this.showHistoricalEAViews = this.historicalEAEnrollments.length > 0;
        
        const histEAItOption = this.historicalEAEnrollments.find(
          (h) => h.ageGradeInstrumentId == AgeGradeInstrument.pilotInfantToddler
        )?.ageGradeTemplateId;
        const histEAPtkOption = this.historicalEAEnrollments.find(
          (h) => h.ageGradeInstrumentCode == AgeGradeInstrumentCode.pilotPreschool
        )?.ageGradeTemplateId;
        const histEAP3Option = this.historicalEAEnrollments.find(
          (h) => h.ageGradeInstrumentCode == AgeGradeInstrumentCode.pilotPreschool3
        )?.ageGradeTemplateId;

        this.historicalEAForm.setValue({
          histEAItTemplateId: histEAItOption ?? null,
          histEAPtkTemplateId: histEAPtkOption ?? null,
          histEAP3TemplateId: histEAP3Option ?? null,
        });
      }

      if (this.show2025Views) {
        const pilotItOption = this.agencyBaseEnrollments.find(
          (a) =>
            a.ageGradeInstrumentId == AgeGradeInstrument.pilotInfantToddler && a.measureRevisionId == MeasureRevision.R2025
        )?.ageGradeTemplateId;
        const ptkOption = this.agencyBaseEnrollments.find(
          (a) =>
            a.ageGradeInstrumentCode == AgeGradeInstrumentCode.pilotPreschool && a.measureRevisionId == MeasureRevision.R2025
        )?.ageGradeTemplateId;
        const p3Option = this.agencyBaseEnrollments.find(
          (a) =>
            a.ageGradeInstrumentCode == AgeGradeInstrumentCode.pilotPreschool3 && a.measureRevisionId == MeasureRevision.R2025
        )?.ageGradeTemplateId;
        const pilotSaOption = this.agencyBaseEnrollments.find(
          (a) =>
            a.ageGradeInstrumentId == AgeGradeInstrument.pilotSchoolAge && a.measureRevisionId == MeasureRevision.R2025
        )?.ageGradeTemplateId;

        this.fieldStudyForm.patchValue({
          pilotItTemplateId: pilotItOption ?? null,
          ptkTemplateId: ptkOption ?? null,
          p3TemplateId: p3Option ?? null,
          pilotSaTemplateId: pilotSaOption ?? null,
        });
      }
    }
  }

  submitForm() {
    if (this.agency?.isRatingViewLocked) return;
    
    if (this.show2015Views && this.defaultViewForm.invalid) {
      this.toast.error(this.translocoService.translate('configuration.ratingViewSetup.defaultTemplatesRequiredToast'));
      return;
    }
    
    if (this.show2025Views && this.fieldStudyForm.invalid) {
      this.toast.error(this.translocoService.translate('configuration.ratingViewSetup.pilotTemplatesRequiredToast'));
      return;
    }

    this.showConfirmationAndSave();
  }

  showConfirmationAndSave() {
    let event = { data: { modalInfo: {} } };
    event.data.modalInfo = {
      title: this.translocoService.translate('configuration.ratingViewSetup.confirmationTitle'),
      message: this.translocoService.translate('configuration.ratingViewSetup.confirmationMessage', { schoolYear: this.schoolYearName }),
      primaryBtnClass: 'blue',
      primaryBtnText: Modal.OK,
      showCancel: true,
    };

    const modalRef = this.dialog.open(ConfirmationModalComponent, {
      data: event,
    });

    modalRef.afterClosed().subscribe((res) => {
      if (res) this.save();
    });
  }

  save() {
    const defaultFormValue = this.defaultViewForm.getRawValue();
    const pilotFormValue = this.fieldStudyForm.getRawValue();

    let allTemplateIds: number[] = [];

    if (this.show2015Views) {
      allTemplateIds.push(
        defaultFormValue.psTemplateId,
        defaultFormValue.itTemplateId,
        defaultFormValue.kTemplateId,
        defaultFormValue.saTemplateId,
        defaultFormValue.tkTemplateId
      );
    }

    if (this.show2025Views) {
      allTemplateIds.push(
        pilotFormValue.pilotItTemplateId,
        pilotFormValue.ptkTemplateId,
        pilotFormValue.p3TemplateId,
        pilotFormValue.pilotSaTemplateId
      );
    }

    allTemplateIds = allTemplateIds.filter((t) => t != null);

    let request: any = {
      agencyId: this.selectedAgencyId,
      templateIds: allTemplateIds
    };

    this.templateEnrollmentService
      .saveBaseTemplateEnrollment(request)
      .pipe(take(1))
      .subscribe((res) => {
        this.getAgencyInfo(this.selectedAgencyId);
      });
  }

  addCustomRatingModal() {
    var event = { data: { modalInfo: {} } };
    event.data.modalInfo = {
      title: this.translocoService.translate('configuration.ratingViewSetup.addAlternateRatingView'),
      message: this.translocoService.translate('configuration.ratingViewSetup.addAlternateInfoMessage'),
      primaryBtnClass: 'blue',
      primaryBtnText: Modal.OK,
      showCancel: true,
    };
    const modalRef = this.dialog.open(ConfirmationModalComponent, {
      data: event ? event : undefined,
    });
    modalRef.afterClosed().subscribe((res) => {
      if (res) {
        this.goToCustomForm();
      }
    });
  }

  goToCustomForm() {
    this.router.navigateByUrl('/config/custom-rating-view', {
      state: { agencyId: this.selectedAgencyId, stateId: this.selectedStateId },
    });
  }

  gotToBaseForm() {
    this.router.navigateByUrl('/config/base-rating-view', {
      state: { agencyId: this.selectedAgencyId, stateId: this.selectedStateId },
    });
  }

  viewCustomForm(data: any) {
    this.router.navigateByUrl(`/config/custom-rating-view/${data.data.id}`, {
      state: { agencyId: this.selectedAgencyId, stateId: this.selectedStateId },
    });
  }

  clearForm() {
    this.canViewResults = false;
  }

  deleteModal(event: IModalEmitData): void {
    const customTemplateId = event.data.id;
    if (event.data.hasOwnProperty('ratingPeriod')) {
      event.data.modalInfo = {
        title: this.translocoService.translate('configuration.common.cannotDeleteTitle', { name: event.data.name }),
        message: this.translocoService.translate('configuration.ratingViewSetup.assignedCannotDeleteMessage'),
        name: event.data.name,
        primaryBtnClass: 'blue',
        primaryBtnText: Modal.OK,
      };
      const modalRef = this.modal.open(ConfirmationModalComponent, {
        data: event ? event : undefined,
      });
      modalRef.afterClosed().subscribe();
    } else {
      event.data.modalInfo = {
        title: this.translocoService.translate('configuration.common.deleteTitle', { name: event.data.name }),
        message: this.translocoService.translate('configuration.common.deleteMessage', { name: event.data.name }),
        name: event.data.name,
        primaryBtnClass: 'red',
        primaryBtnText: Modal.Delete,
        showCancel: true,
      };

      const modalRef = this.modal.open(ConfirmationModalComponent, {
        data: event ? event : undefined,
      });
      modalRef.afterClosed().subscribe((res) => {
        if (res) {
          this.templateService
            .deleteAgeGradeTemplate(customTemplateId)
            .subscribe((res: boolean) => {
              if (res) {
                this.toast.success(
                  this.translocoService.translate('configuration.ratingViewSetup.deletedToast', { name: event.data.name })
                );
                this.dataSource.data = this.dataSource.data.filter(
                  (item: any) => item.id !== event.data
                );
              } else {
                this.toast.error(
                  this.translocoService.translate('configuration.ratingViewSetup.deleteFailedToast', { name: event.data.name })
                );
              }
              this.initData();
            });
        }
      });
    }
  }
}